<script setup lang="ts">
// Delete-a-profile confirmation with the "still in use? force-detach retry"
// loop the plain confirmDialog() helper cannot express. Closes with the list
// of user uids that had this profile detached (empty if unassigned before
// delete), or undefined on cancel/dismiss.

import { ref } from 'vue'

import type { CapabilityProfile } from '../types/callables'
import type { DeleteResult } from '../composables/useCapabilityProfiles'
import BaseDialog from '../ui/components/BaseDialog.vue'
import { useDialog } from '../ui/dialogs'

export interface ConfirmDeleteResult {
  detachedFrom: string[]
}

const props = defineProps<{
  profile:         CapabilityProfile
  initialAssigned: number
  remove:          (force: boolean) => Promise<DeleteResult>
}>()

const { close } = useDialog<ConfirmDeleteResult>()

// Track the currently-known assignment count so the dialog can update its
// warning text if the server's fresh count differs from initialAssigned
// (an admin in another tab assigned this profile between the panel loading
// its counts and the delete attempt).
const assigned = ref(props.initialAssigned)
const reason   = ref<string | null>(null)
const busy     = ref(false)

async function attempt() {
  busy.value = true
  reason.value = null

  // Only force when we already know there are assignments. First click on an
  // unassigned profile calls remove(false); the server can still refuse if
  // the count moved under us, in which case we surface the fresh count and
  // require a second click.
  const force = assigned.value > 0
  const result = await props.remove(force)
  busy.value = false

  if (result.kind === 'ok') {
    close({ detachedFrom: result.detachedFrom })
    return
  }

  if (result.kind === 'in-use') {
    assigned.value = result.count
    reason.value = `This profile is currently assigned to ${result.count} user${result.count === 1 ? '' : 's'}. Deleting will remove it from their assignments.`
    return
  }

  reason.value = result.message
}
</script>

<template>
  <BaseDialog title="Delete profile" size="narrow" @dismiss="close()">
    <p>Delete <strong>{{ profile.name }}</strong>?</p>

    <p v-if="reason" class="field-error">{{ reason }}</p>
    <p v-else-if="assigned > 0" class="field-error">
      Currently assigned to {{ assigned }}
      user{{ assigned === 1 ? '' : 's' }}. Deleting will
      remove it from their assignments.
    </p>

    <template #actions>
      <button type="button" class="btn btn-secondary" :disabled="busy" @click="close()">
        Cancel
      </button>
      <button type="button" class="btn btn-danger" :disabled="busy" @click="attempt">
        {{ busy
            ? 'Deleting…'
            : assigned > 0 ? 'Delete and detach' : 'Delete' }}
      </button>
    </template>
  </BaseDialog>
</template>
